import { useCallback, useEffect, useId, useRef } from 'react';
import { useBeforeUnload, useBlocker } from 'react-router';
import { useU2000FormLeaveGuardContext } from '../context/formLeaveGuard/U2000FormLeaveGuardContext';
import { U2000FormStateEnumState } from '../reducer/formReducer/U2000FormState';
import type { U2000FormStateHandlers } from '../reducer/formReducer/U2000FormStateHandlers';
import { u2000IsFormRowDirty } from '../tools/u2000FormDirtyUtils';

/**
 * Bloque la navigation SPA et le déchargement navigateur si fiche en édition avec saisie modifiée (BL-0635).
 */
export function useU2000FormLeaveGuard(formStateHandlers: U2000FormStateHandlers<Record<string, unknown>>) {
  const registrationId = useId();
  const { setFormLeaveBlocking, unregisterFormLeaveBlocking, promptFormLeave, navigationBypassRef } =
    useU2000FormLeaveGuardContext();

  const inEdit = formStateHandlers.getFormState() === U2000FormStateEnumState.EDIT;
  const dirty = u2000IsFormRowDirty(formStateHandlers.getData(), formStateHandlers.getOldData());
  const shouldBlock = inEdit && dirty;

  useEffect(() => {
    setFormLeaveBlocking(registrationId, shouldBlock);
    return () => unregisterFormLeaveBlocking(registrationId);
  }, [registrationId, shouldBlock, setFormLeaveBlocking, unregisterFormLeaveBlocking]);

  const blocker = useBlocker(
    useCallback(() => {
      if (navigationBypassRef.current) {
        return false;
      }
      return shouldBlock;
    }, [shouldBlock, navigationBypassRef]),
  );

  useBeforeUnload(
    useCallback(
      (event) => {
        if (shouldBlock) {
          event.preventDefault();
        }
      },
      [shouldBlock],
    ),
  );

  const blockerPromptedRef = useRef(false);

  useEffect(() => {
    if (blocker.state !== 'blocked') {
      blockerPromptedRef.current = false;
      return;
    }
    if (blockerPromptedRef.current) {
      return;
    }
    if (!shouldBlock) {
      // Saisie commitée entre le blocage et ce rendu (ex. navigate puis afterSaveData) : plus rien à perdre.
      // Uniquement avant affichage du dialogue — sinon l’utilisateur garde la main (Confirmer / Annuler).
      blocker.proceed();
      return;
    }
    blockerPromptedRef.current = true;
    promptFormLeave({
      onConfirm: () => {
        const confirmFormLeaveWithDirty = formStateHandlers.getBusinessHandlers()?.confirmFormLeaveWithDirty;
        if (confirmFormLeaveWithDirty) {
          void Promise.resolve(confirmFormLeaveWithDirty()).finally(() => {
            if (blocker.state === 'blocked') {
              blocker.reset();
            }
          });
          return;
        }
        if (blocker.state === 'blocked') {
          blocker.proceed();
        }
      },
      onCancel: () => {
        if (blocker.state === 'blocked') {
          blocker.reset();
        }
      },
    });
  }, [blocker.state, blocker, shouldBlock, formStateHandlers, promptFormLeave]);
}
